import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "One awkward thing about MobX: Complex models",
  "description": "",
  "published": "2016-12-02T00:00:00.000Z",
  "redirect_from": ["/blog/awkward-thing-mobx-complex-models/swizec/7260"],
  "categories": "Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Let's take an example from `}<a parentName="p" {...{
        "href": "https://www.yup.com"
      }}>{`my day job`}</a>{`. An onboarding step for new tutors is a subject exam. It starts with this page:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/Screen-Shot-2016-12-02-at-07.59.53.png",
        "alt": null
      }}></img></p>
    <p>{`In this case, the applicant has already flunked chemistry, we're not accepting physics, and they can still take a math exam. To build this, we need data about the current user, about the subjects, and how they relate to each other.`}</p>
    <h2 {...{
      "id": "basic-api-backed-mobx-model"
    }}>{`Basic API-backed MobX model`}</h2>
    <p>{`Building a basic model with MobX is straightforward once you discover `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{`. It's a store that gets its properties from the API.`}</p>
    <p>{`A `}<inlineCode parentName="p">{`User`}</inlineCode>{` model might look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class User {
  constructor(mainStore, id) {
    this.mainStore = mainStore;
    this.id = id;

    this.fetchFromAPI();
  }

  @computed get url() {
    return \`/api/users/\${id}\`;
  }

  @action fetchFromAPI() {
    fetch(this.url)
      .then((response) => response.json())
      .then(
        action("set-user-params", (json) => {
          extendObservable(json);
        })
      );
  }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`constructor`}</inlineCode>{` takes a user `}<inlineCode parentName="p">{`id`}</inlineCode>{` and a `}<inlineCode parentName="p">{`mainStore`}</inlineCode>{` reference. Having that reference to a parent store is often useful when spelunking through your data model. I learned that the hard way ?`}</p>
    <p>{`We call `}<inlineCode parentName="p">{`fetchFromAPI`}</inlineCode>{` to fetch the user data, use a `}<inlineCode parentName="p">{`url`}</inlineCode>{` computed value for the URL, and naively use `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{` to set all the API data as observables on our `}<inlineCode parentName="p">{`User`}</inlineCode>{` model.`}</p>
    <p>{`This approach embraces `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Duck_typing"
      }}>{`duck typing`}</a>{`. There's no advance declaration of what a user model looks like. No description of properties, nothing you can rely on just by looking at the code. You're just gonna have to try to access a property and hope it works.`}</p>
    <p>{`But when your model looks like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/12/Screen-Shot-2016-12-02-at-08.21.44.png",
        "alt": null
      }}></img></p>
    <p>{`Do you really want to type all of that out? No, you don't.`}</p>
    <p><inlineCode parentName="p">{`extendObservable`}</inlineCode>{` is a convenient approach for these situations. It gives you infinite flexibility and makes properties deeply observable.`}</p>
    <p>{`That means using things like `}<inlineCode parentName="p">{`user.subject_statuses[0].can_start_exam`}</inlineCode>{` Just Works™. You should be careful about undefineds, though. Until the API call goes through, anything beyond `}<inlineCode parentName="p">{`user.`}</inlineCode>{` is undefined. That part is annoying.`}</p>
    <p>{`Sidenote:`}</p>
    <p>{`You can fake a `}<a parentName="p" {...{
        "href": "#"
      }}>{`maybe monad`}</a>{` in MobX using `}<inlineCode parentName="p">{`when`}</inlineCode>{`, like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`when(() => user.subject_statuses,
        () => // do stuff with data)
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "/ebdad828baf539c9bfa7ca83faf5cabb/media-IeLOBZb7ZdQ1G-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "connecting-models-is-where-things-get-awkward"
    }}>{`Connecting models is where things get awkward`}</h2>
    <p>{`Let's have a `}<inlineCode parentName="p">{`mainStore`}</inlineCode>{` that looks a lot like the `}<inlineCode parentName="p">{`User`}</inlineCode>{` model above. It may look like the `}<inlineCode parentName="p">{`User`}</inlineCode>{` model, but it fetches a bunch of things about the environment, one of which is the current user's id, another of which is a list of subjects.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const MainStore {
    @observable currentUser = null;

    constructor() {
        fetch('/api/environment_data')
            .then(response => response.json())
            .then(this.setData);
    }

    @action setData(json) {
        this.currentUser = new User(this, json.currentUser.id);
        delete json.currentUser;

        extendObservable(this, json);
    }
}
`}</code></pre>
    <p>{`Whoa, did you see that? We fetched data, took the `}<inlineCode parentName="p">{`currentUser.id`}</inlineCode>{`, then deleted `}<inlineCode parentName="p">{`currentUser`}</inlineCode>{` before using `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{`. Wat.`}</p>
    <p>{`The API returns an object with many properties. In our example, let's assume it looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
    currentUser: {
        id: N,
        // other stuff, maybe
    },
    subjects: [
        {id: N, name: 'Math'},
        {id: N, name: 'Chemistry'},
        // ...
    ]
}
`}</code></pre>
    <p>{`We want to avoid writing a bunch of boilerplate, so we use the `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{` trick to set everything. But we don't want to overwrite our custom `}<inlineCode parentName="p">{`currentUser`}</inlineCode>{`, which is a proper MobX model, not just an observable.`}</p>
    <p>{`So we have to delete that part of the data before extending. Awkward.`}</p>
    <p>{`And `}<em parentName="p">{`why`}</em>{` do we want `}<inlineCode parentName="p">{`User`}</inlineCode>{` to be more than just an observable? Because it's going to need `}<inlineCode parentName="p">{`@computed`}</inlineCode>{` values. We might re-fetch the model at random points or perform many actions that save stuff to the backend, change frontend state, and so on.`}</p>
    <p>{`Dealing with all of that in `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{` would get hairy fast.`}</p>
    <p>{`You're right. From the perspective of this example, that's a total `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/You_aren't_gonna_need_it"
      }}>{`YAGNI`}</a>{` - ya ain't gonna need it. But bear with me.`}</p>
    <h2 {...{
      "id": "add-the-domain-logic-increase-the-awkward"
    }}>{`Add the domain logic, increase the awkward`}</h2>
    <p>{`Using the above `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{`, rendering a subject picker is straight forward. Loop through observable list of subjects, render elements. If something changes, MobX takes care of it.`}</p>
    <p>{`Not that the number or naming of subjects ever changes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const SubjectPicker = inject('mainStore')(observer(({ mainStore }) => {
    if (mainStore.subjects) {
        return (
            <div>
                {mainStore.subjects.map((s, i) =>
                    <subject subject={s} key={i}>
                 )}
            </subject></div>
        );
    } else {
        return null;
    }
}));
`}</code></pre>
    <p>{`Assume we put an instance of `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{` in a \``}<inlineCode parentName="p">{`as `}</inlineCode>{`mainStore`}<inlineCode parentName="p">{`. That gives us global access via `}</inlineCode>{`inject\` and cleans up our codebase.`}</p>
    <p>{`A simplified \`\`component would look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Subject = observer(({ subject }) => (
  <div>
    <h3>{subject.name}</h3>
    {subject.userStatus.can_start_exam ? "Go for it" : "LoL Nope."}
  </div>
));
`}</code></pre>
    <p>{`Not as pretty as the screenshot, but all that HTML and styling is irrelevant right now, so I took it out. Ours renders the subject name in an `}<inlineCode parentName="p">{`h3`}</inlineCode>{` tag, and a string `}<inlineCode parentName="p">{`Go for it`}</inlineCode>{` if you can take this exam or `}<inlineCode parentName="p">{`LoL Nope`}</inlineCode>{` if you can't.`}</p>
    <p>{`Now, the trouble; can you see it?`}</p>
    <p>{`It's that `}<inlineCode parentName="p">{`subject.userStatus.can_start_exam`}</inlineCode>{` bit. You see, we've found ourselves staring at a piece of data that lies `}<em parentName="p">{`between`}</em>{` concepts.`}</p>
    <p>{`Ask a subject if it's startable and it needs to know who you're asking about. Ask a user and it needs to know which subject you're asking about. It `}<em parentName="p">{`smells`}</em>{` like a computable value, but where?`}</p>
    <p>{`Computables can't take arguments.`}</p>
    <p>{`So even though the `}<inlineCode parentName="p">{`User`}</inlineCode>{` model has this data – stored in `}<inlineCode parentName="p">{`subject_statuses`}</inlineCode>{` – we can't put the computed there. It can't go in `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{` either because that too would require arguments.`}</p>
    <p><em parentName="p">{`"Dude, it's called a method. Just use a method"`}</em>{`, right?`}</p>
    <p>{`Yes, methods and functions can take arguments. But MobX computeds are preferable because they automagically observe values, memoize their results, and so on. Cleaner code. Faster code.`}</p>
    <p>{`We need a new `}<inlineCode parentName="p">{`Subject`}</inlineCode>{` model that knows about `}<inlineCode parentName="p">{`currentUser`}</inlineCode>{`. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Subject {
  @observable currentUser = null;

  constructor(currentUser, data) {
    this.currentUser = currentUser;
    extendObservable(this, data);
  }

  @computed get userStatus() {
    return this.currentUser.subject_statuses.find(
      (s) => this.id === s.subject_id
    );
  }
}
`}</code></pre>
    <p>{`It takes a reference to `}<inlineCode parentName="p">{`currentUser`}</inlineCode>{` and raw data about itself then uses `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{` to set it all up. We've added a computed `}<inlineCode parentName="p">{`userStatus`}</inlineCode>{` method that goes to `}<inlineCode parentName="p">{`currentUser`}</inlineCode>{` and finds the correct data.`}</p>
    <p>{`This part is awkward, too. A model that returns information from a different model? Wat.`}</p>
    <p>{`I'm not sure. Maybe it's dumb. Maybe I'm dumb. I think it makes sense.`}</p>
    <p>{`Anyway, in `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{`, we now do something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    @action setData(json) {
        this.currentUser = new User(this, json.currentUser.id);
        delete json.currentUser;

        this.subjects = json.subjects.map(
            s => new Subject(this.currentUser, s)
        );
        delete json.subjects;

        extendObservable(this, json);
    }
`}</code></pre>
    <p>{`Not very elegant, is it? And I think the `}<inlineCode parentName="p">{`subjects`}</inlineCode>{` array stopped being observable in the process, so we have to add it as an `}<inlineCode parentName="p">{`@observable`}</inlineCode>{` property explicitly.`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <ol>
      <li parentName="ol">{`Use `}<inlineCode parentName="li">{`extendObservable`}</inlineCode>{` to load and create large models`}</li>
      <li parentName="ol">{`Create new models when submodels need `}<inlineCode parentName="li">{`@action`}</inlineCode>{`s or `}<inlineCode parentName="li">{`@computed`}</inlineCode>{`s`}</li>
      <li parentName="ol">{`Do the `}<inlineCode parentName="li">{`new Model`}</inlineCode>{` then `}<inlineCode parentName="li">{`delete`}</inlineCode>{` dance when that happens`}</li>
    </ol>
    <p>{`It would be nice if `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{` could do this for us. I'm sure it's possible as long as model constructors are forced to follow a common pattern.`}</p>
    <p>{`But maybe that exists already and I don't know about it? Would def be a fun opensource project. ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      